<script lang="ts" setup>
  import { useAuthStore } from '@tundr/auth';
  import {
    Button,
    ButtonSize,
    ButtonVariant,
    Dropdown,
    DropdownItem,
    DropdownPlacement,
    Tooltip,
    TooltipPosition,
    FontIcon,
  } from '@tundr/ds-components';
  import { useLocale } from '@tundr/i18n';
  import { useModal } from '@tundr/modal';
  import { TUNDR_ICONS } from '@tundr/theme-assets/tundr-icons/tundr-icons-icons';
  import { computed, defineAsyncComponent } from 'vue';
  import {
    companyActionsClass,
    companyMenuContentClass,
    companyNameClass,
    menuTitleClass,
    selectedCompanyNameTextClass,
    sidebarCompanyMenuPopoverTriggerClass,
    sidebarCompanyMenuTriggerHostClass,
    sidebarCompanyRowClass,
    sidebarCompanyRowExpandedClass,
  } from './SidebarCompanyRow.css';

  const props = defineProps<{
    collapsed: boolean;
  }>();

  const { t } = useLocale('common');
  const { openModal } = useModal();

  const authStore = useAuthStore();

  const SwitchCompanyModal = defineAsyncComponent(
    () => import('../SwitchCompanyModal/SwitchCompanyModal.vue'),
  );

  const AddNewCompanyEmailCodeModal = defineAsyncComponent(
    () =>
      import(
        '@modules/app/user/components/AddNewCompanyEmailCodeModal/AddNewCompanyEmailCodeModal.vue'
      ),
  );

  const onSwitchCompany = () => {
    openModal({
      component: SwitchCompanyModal,
      config: {
        blocking: false,
      },
    });
  };

  const onAddNewCompany = () => {
    openModal({
      component: AddNewCompanyEmailCodeModal,
      config: {
        blocking: true,
      },
    });
  };

  const sidebarCompanyRowClasses = computed(() => [
    sidebarCompanyRowClass,
    {
      [sidebarCompanyRowExpandedClass]: !props.collapsed,
    },
  ]);

  const selectedCompanyName = computed(() => authStore.selectedCompany?.name);

  const companyNameTooltipTitle = computed(
    () => selectedCompanyName.value?.trim() || undefined,
  );

  const collapsedCompanyMenuAriaLabel = computed(() =>
    selectedCompanyName.value
      ? t('profile_menu.actions.open_company_menu.with_name', {
          name: selectedCompanyName.value,
        })
      : t('profile_menu.actions.open_company_menu.aria_label'),
  );

  /** DS Tooltip title: company name, or menu aria string when the name is missing. */
  const collapsedCompanyTooltipTitle = computed(
    () => companyNameTooltipTitle.value ?? collapsedCompanyMenuAriaLabel.value,
  );
</script>

<template>
  <div :class="sidebarCompanyRowClasses">
    <template v-if="!collapsed">
      <div :class="companyNameClass">
        <FontIcon :icon-name="TUNDR_ICONS.BUILDING" />
        <Tooltip
          :title="companyNameTooltipTitle"
          :position="TooltipPosition.RIGHT"
          :interactive="false"
        >
          <div :class="selectedCompanyNameTextClass">
            {{ selectedCompanyName }}
          </div>
        </Tooltip>
      </div>

      <div :class="companyActionsClass">
        <Tooltip
          v-if="authStore.userCompanies && authStore.userCompanies.length > 1"
          :title="t('profile_menu.actions.switch_company.label')"
          :position="TooltipPosition.RIGHT"
          :interactive="false"
        >
          <Button
            :variant="ButtonVariant.Icon"
            :icon-name="TUNDR_ICONS.SWITCH"
            is-ghost
            :size="ButtonSize.SM"
            :aria-label="t('profile_menu.actions.switch_company.label')"
            test-id="company-action-switch-company"
            @click="onSwitchCompany"
          />
        </Tooltip>
        <Tooltip
          :title="t('profile_menu.actions.add_company.label')"
          :position="TooltipPosition.RIGHT"
          :interactive="false"
        >
          <Button
            :variant="ButtonVariant.Icon"
            :icon-name="TUNDR_ICONS.PLUS"
            is-ghost
            :size="ButtonSize.SM"
            :aria-label="t('profile_menu.actions.add_company.label')"
            test-id="company-action-add-company"
            @click="onAddNewCompany"
          />
        </Tooltip>
      </div>
    </template>
    <Dropdown
      v-else
      :placement="DropdownPlacement.BOTTOM_END"
      :trigger-as-child="false"
      :trigger-full-width="true"
      :slot-trigger-class="sidebarCompanyMenuPopoverTriggerClass"
    >
      <template #trigger>
        <Tooltip
          :title="collapsedCompanyTooltipTitle"
          :position="TooltipPosition.RIGHT"
          :interactive="false"
        >
          <div
            :class="sidebarCompanyMenuTriggerHostClass"
            :aria-label="collapsedCompanyMenuAriaLabel"
          >
            <FontIcon
              :icon-name="TUNDR_ICONS.BUILDING"
              test-id="company-menu-trigger"
            />
          </div>
        </Tooltip>
      </template>
      <div :class="companyMenuContentClass">
        <div :class="menuTitleClass">{{ selectedCompanyName }}</div>
        <DropdownItem
          :icon="TUNDR_ICONS.SWITCH"
          test-id="menu-item-switch-company"
          @click="onSwitchCompany"
          :label="t('profile_menu.actions.switch_company.label')"
        />
        <DropdownItem
          :icon="TUNDR_ICONS.PLUS"
          test-id="menu-item-add-company"
          @click="onAddNewCompany"
          :label="t('profile_menu.actions.add_company.label')"
        />
      </div>
    </Dropdown>
  </div>
</template>
